import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import filter from "lodash/filter";
import { nanoid } from "nanoid";
import Button from "shared/Button";
import { Icon } from "components/SvgIcons";
import { colors } from "styles/colors";
import { deleteGrocery } from "api/api";
import GroceryContext from "./GroceryContext";
import GroceryItem from "./GroceryItem";
import EmailModal from "./EmailModal";
import CustomGrocery from "./Custom/CustomGrocery";
import recalculateNames from "./recalculateNames";
import { Title, TitleText, TitleButtons, ListContainer, EmptyPlace } from "./styled";

import { createAlert } from "store/alert";
import {
  getGroceryList,
  clearAllGroceries,
  patchGrocery,
  sendGroceryList,
  getCustomGrocery,
  updateCustomGrocery,
} from "api/api";

const GroceryList = () => {
  const [groceryList, setGroceryList] = useState([]);
  const [emailModal, setEmailModal] = useState(false);
  const dispatch = useDispatch();

  const [customItems, setCustomItems] = useState([]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customEdit, setCustomEdit] = useState(null);

  const closeCustomIngredientInput = () => {
    setShowCustomInput(false);
  };

  const addCustomIngredientItem = async (name) => {
    const customItem = {
      name,
      id: nanoid(),
      check: false,
      deleted: false,
    };

    let items = customItems.length < 1 ? [customItem] : [customItem, ...customItems];
    setShowCustomInput(false);
    setCustomItems(items);
    await updateCustomGrocery(items);
  };

  const updateCustomIngredientItem = async (name, { id }) => {
    const items = cloneDeep(customItems);
    const item = find(items, { id });
    item.name = name;
    setShowCustomInput(false);
    setCustomItems(items);
    await updateCustomGrocery(items);
  };

  const toggleCustomItem = async ({ id }) => {
    const items = cloneDeep(customItems);
    const item = find(items, { id });
    item.check = !item.check;
    setCustomItems(items);
    await updateCustomGrocery(items);
  };

  const removeCustomItem = async (customId) => {
    const items = customItems.filter(({ id }) => id != customId);
    setCustomItems(items);
    await updateCustomGrocery(items);
  };

  const editCustomItem = async (id) => {
    const item = find(customItems, { id });
    setShowCustomInput(true);
    setCustomEdit(item);
  };

  const toggleItem = async ({ groceryId, ingredientId, id }) => {
    const clonedList = cloneDeep(groceryList);
    const grocery = find(clonedList, { id: groceryId });
    const ingredient = find(grocery.ingredients, { id: ingredientId });
    const item = find(ingredient.items, { id });
    item.check = !item.check;
    setGroceryList(clonedList);
    await patchGrocery(grocery.id, { ingredients: grocery.ingredients, yields: grocery.yields });
  };

  const updateYields = async (groceryId, count) => {
    const clonedList = cloneDeep(groceryList);
    const grocery = find(clonedList, { id: groceryId });
    const { value, original_value } = grocery.yields;

    if (count == "minus" && value <= original_value) {
      return;
    }

    let nextValue = count == "plus" ? value + original_value : value - original_value;
    grocery.yields.value = nextValue;
    recalculateNames(grocery, nextValue);
    setGroceryList(clonedList);
    await patchGrocery(grocery.id, { ingredients: grocery.ingredients, yields: grocery.yields });
  };

  const removeGroceryItem = async (groceryId) => {
    const list = filter(groceryList, ({ id }) => id != groceryId);
    setGroceryList(list);
    await deleteGrocery(groceryId);
  };

  const clearAll = async () => {
    setGroceryList([]);
    setCustomItems([]);
    await clearAllGroceries();
  };

  useEffect(async () => {
    const {
      data: { count, results },
    } = await getGroceryList();
    const { data } = await getCustomGrocery();
    setCustomItems(data[0]?.items || []);
    if (count) {
      setGroceryList(results);
    }
  }, []);

  const sendEmail = async (email) => {
    try {
      setEmailModal(false);
      await sendGroceryList(email);
      dispatch(createAlert({ type: "success", message: "Sent successfully!" }));
    } catch (e) {
      dispatch(createAlert({ type: "error", message: e?.response?.data?.message || "network error" }));
    }
  };

  const showGroceries = showCustomInput || customItems.length > 0 || groceryList.length > 0;

  return (
    <>
      <Title>
        <TitleText>
          Grocery List
          <Icon icon="plus" onClick={editCustomItem} />
        </TitleText>
        <TitleButtons>
          <Button
            className="hide-mobile"
            color={colors.WHITE}
            textColor={colors.GREY_DARK}
            stylType="outline"
            text="Add Ingredient"
            iconRight="plus"
            onClick={editCustomItem}
          />
          <Button
            color={colors.WHITE}
            textColor={colors.GREY_DARK}
            stylType="outline"
            text="Clear All"
            onClick={clearAll}
          />
          <Button
            color={colors.RED}
            textColor={colors.WHITE}
            stylType="outline"
            text="Send via Email"
            iconRight="email2"
            onClick={() => setEmailModal(true)}
          />
        </TitleButtons>
      </Title>
      {showGroceries ? (
        <GroceryContext.Provider
          value={{
            toggleItem,
            updateYields,
            removeGroceryItem,
            closeCustomIngredientInput,
            addCustomIngredientItem,
            updateCustomIngredientItem,
            toggleCustomItem,
            removeCustomItem,
            editCustomItem,
          }}
        >
          <ListContainer>
            <CustomGrocery customItems={customItems} showCustomInput={showCustomInput} customEdit={customEdit} />
            {groceryList.map((grocery) => (
              <GroceryItem key={grocery.id} grocery={grocery} />
            ))}
          </ListContainer>
        </GroceryContext.Provider>
      ) : (
        <EmptyPlace>Add recipes from your library or search to get started on your grocery list</EmptyPlace>
      )}
      {emailModal && <EmailModal onCancel={() => setEmailModal(false)} onConfirm={sendEmail} />}
    </>
  );
};

export default GroceryList;
